import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash, ChatCenteredDots, ArrowUpRight, PencilSimple, Heart, ChatCircle, UserPlus, X, Lightning } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmProvider";
import CommentSuggestModal from "@/components/CommentSuggestModal";
import CommentsInsights from "@/components/CommentsInsights";

const EMPTY_FORM = {
  comment_text: "",
  author_name: "",
  post_url: "",
  post_context: "",
  reactions: 0,
  replies: 0,
  connections_from_it: 0,
};

export default function CommentsPage() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showAdd, setShowAdd] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [tab, setTab] = useState("log"); // "log" | "insights"
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/comments");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    count: items.length,
    reactions: items.reduce((a, c) => a + (c.reactions || 0), 0),
    replies: items.reduce((a, c) => a + (c.replies || 0), 0),
    connections: items.reduce((a, c) => a + (c.connections_from_it || 0), 0),
  }), [items]);

  const openAdd = () => { setForm(EMPTY_FORM); setEditingId(null); setShowAdd(true); };
  const openEdit = (c) => {
    setForm({
      comment_text: c.comment_text || "",
      author_name: c.author_name || "",
      post_url: c.post_url || "",
      post_context: c.post_context || "",
      reactions: c.reactions || 0,
      replies: c.replies || 0,
      connections_from_it: c.connections_from_it || 0,
    });
    setEditingId(c.id);
    setShowAdd(true);
  };
  const save = async (e) => {
    e.preventDefault();
    if (!form.comment_text.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/comments/${editingId}`, form);
        toast.success("Comment updated.");
      } else {
        await api.post("/comments", form);
        toast.success("Comment saved. She'll learn from your voice.");
      }
      setShowAdd(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async (c) => {
    const ok = await confirm({
      title: "Delete this comment?",
      description: `"${(c.comment_text || '').slice(0, 80)}${(c.comment_text||'').length>80?'…':''}" will be moved to Trash.`,
      confirmText: "Move to Trash",
    });
    if (!ok) return;
    await api.delete(`/comments/${c.id}`);
    toast.success("Moved to Trash. Restore anytime.");
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Comments"
        title="Where you show up in other people's threads."
        subtitle="Track every meaningful comment you make on LinkedIn. She uses these to learn your point of view, your contrarian instincts, and how you take a stand in real conversations."
        action={
          <div className="flex items-center gap-2">
            <button
              data-testid="comment-suggest-btn"
              className="btn btn-ghost"
              onClick={() => setShowSuggest(true)}
              title="Paste a post + your PoV — get a comment drafted in your voice."
            >
              <Lightning size={16} weight="fill"/> Suggest a comment
            </button>
            <button data-testid="comment-add-btn" className="btn btn-accent" onClick={openAdd}><Plus size={16}/> Log a comment</button>
          </div>
        }
      />
      <CommentSuggestModal
        open={showSuggest}
        onClose={() => setShowSuggest(false)}
        onSavedAsComment={load}
      />

      <div className="mb-6 flex items-center gap-2">
        <div className="seg" role="tablist" aria-label="View" data-testid="comments-tabs">
          <button onClick={() => setTab("log")}      data-active={tab === "log"}      data-testid="comments-tab-log">Log</button>
          <button onClick={() => setTab("insights")} data-active={tab === "insights"} data-testid="comments-tab-insights">Insights</button>
        </div>
      </div>

      {tab === "insights" ? (
        <CommentsInsights />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              ["Comments logged", totals.count],
              ["Reactions earned", totals.reactions],
              ["Replies to you", totals.replies],
              ["Connections from comments", totals.connections],
            ].map(([label, val]) => (
              <div key={label} className="card p-5 fade-up">
                <div className="eyebrow mb-2">{label}</div>
                <div className="font-serif text-3xl tracking-tight font-semibold">{val.toLocaleString()}</div>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="text-sm" style={{ color: "var(--ink-3)" }}>Loading…</div>
          ) : items.length === 0 ? (
        <div className="card p-16 text-center">
          <ChatCenteredDots size={26} className="mx-auto mb-3" style={{ color: "var(--accent)" }}/>
          <div className="eyebrow mb-2">Nothing yet</div>
          <p className="font-serif text-3xl mb-3">Comments are underrated leverage.</p>
          <p className="text-sm max-w-md mx-auto" style={{ color: "var(--ink-2)" }}>
            The comment section is where you get in front of your dream connections' feeds without ever posting. Log every meaningful one, she'll learn from your voice and correlate it to your growth.
          </p>
          <button data-testid="comment-empty-add" onClick={openAdd} className="btn btn-accent mt-6"><Plus size={16}/> Log your first comment</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="comments-list">
          {items.map((c) => (
            <div key={c.id} data-testid={`comment-${c.id}`} className="card p-5 fade-up">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  {c.author_name && <span className="chip">on <strong className="ml-1 truncate max-w-[200px] inline-block align-bottom">{c.author_name}</strong>'s post</span>}
                  <span className="text-xs font-mono" style={{ color: "var(--ink-3)" }}>{new Date(c.created_at).toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'})}</span>
                  {c.post_url && (
                    <a data-testid={`comment-link-${c.id}`} href={c.post_url} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1" style={{color:"var(--accent-2)"}}>
                      Original <ArrowUpRight size={11}/>
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`comment-edit-${c.id}`} onClick={()=>openEdit(c)} className="p-2 rounded-full hover:bg-[color:var(--paper)]" title="Edit"><PencilSimple size={14}/></button>
                  <button data-testid={`comment-delete-${c.id}`} onClick={()=>remove(c)} className="p-2 rounded-full hover:bg-[color:var(--paper)]" style={{color:"#b83a2b"}} title="Delete"><Trash size={14}/></button>
                </div>
              </div>
              {c.post_context && (
                <div className="text-xs mb-2 px-3 py-2 rounded-2xl" style={{background:'var(--bg-soft)', color:'var(--ink-2)'}}>
                  <span className="eyebrow mr-1">Their post:</span>{c.post_context}
                </div>
              )}
              <div className="text-sm whitespace-pre-wrap mb-3">{c.comment_text}</div>
              <div className="flex flex-wrap gap-3 text-xs" style={{ color: "var(--ink-3)" }}>
                <span className="flex items-center gap-1"><Heart size={11} weight="fill" style={{color:"var(--accent)"}}/> {c.reactions || 0}</span>
                <span className="flex items-center gap-1"><ChatCircle size={11} weight="fill"/> {c.replies || 0} replies</span>
                <span className="flex items-center gap-1"><UserPlus size={11} weight="fill"/> {c.connections_from_it || 0} connections</span>
              </div>
            </div>
          ))}
        </div>
      )}
        </>
      )}

      {showAdd && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto"
          style={{background:'rgba(0,0,0,0.55)'}}
          data-testid="comment-form-backdrop"
        >
          <form onSubmit={save} className="card w-full max-w-2xl p-6 fade-up" data-testid="comment-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl font-semibold">{editingId ? "Edit comment" : "Log a comment"}</h3>
              <button type="button" onClick={()=>setShowAdd(false)} className="p-2 rounded-full hover:bg-[color:var(--paper)]"><X size={16}/></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <label className="block"><div className="eyebrow mb-1">Whose post?</div>
                <input data-testid="comment-author" className="input" placeholder="e.g. Justin Welsh" value={form.author_name} onChange={e=>setForm({...form, author_name:e.target.value})}/>
              </label>
              <label className="block"><div className="eyebrow mb-1">Post URL (optional)</div>
                <input data-testid="comment-url" className="input" placeholder="https://linkedin.com/…" value={form.post_url} onChange={e=>setForm({...form, post_url:e.target.value})}/>
              </label>
            </div>
            <label className="block mb-3"><div className="eyebrow mb-1">What was the post about? (optional, helps context)</div>
              <textarea data-testid="comment-context" className="textarea" rows={2} placeholder="One-liner about what the OP was saying" value={form.post_context} onChange={e=>setForm({...form, post_context:e.target.value})}/>
            </label>
            <label className="block mb-3"><div className="eyebrow mb-1">Your comment</div>
              <textarea required data-testid="comment-text" className="textarea" rows={5} placeholder="What did you write? Paste it exactly, punctuation and all." value={form.comment_text} onChange={e=>setForm({...form, comment_text:e.target.value})}/>
            </label>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <label className="block"><div className="eyebrow mb-1">Reactions</div>
                <input data-testid="comment-reactions" type="number" min="0" className="input" value={form.reactions} onChange={e=>setForm({...form, reactions:parseInt(e.target.value||"0")})}/>
              </label>
              <label className="block"><div className="eyebrow mb-1">Replies to you</div>
                <input data-testid="comment-replies" type="number" min="0" className="input" value={form.replies} onChange={e=>setForm({...form, replies:parseInt(e.target.value||"0")})}/>
              </label>
              <label className="block"><div className="eyebrow mb-1">Connections from it</div>
                <input data-testid="comment-connections" type="number" min="0" className="input" value={form.connections_from_it} onChange={e=>setForm({...form, connections_from_it:parseInt(e.target.value||"0")})}/>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={()=>setShowAdd(false)} className="btn btn-ghost">Cancel</button>
              <button type="submit" disabled={saving || !form.comment_text.trim()} data-testid="comment-save" className="btn btn-accent">{saving ? "Saving…" : (editingId ? "Update" : "Save comment")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
